import {trans} from '@frontend/helpers'
import type {AdLocation} from '@frontend/modules/public/ads/AdsModuleTypes'
import {isLocationSelected} from '@frontend/modules/public/ads/components/search/helpers/isLocationSelected'
import clsx from 'clsx'
import {
    useMemo,
    useRef,
} from 'react'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {
    type GroupedVirtuosoHandle,
    useVirtuosoLibAsync,
} from 'swayok-react-mdb-ui-kit/helpers/vendor/useVirtuosoLibAsync'

interface Props {
    levels: AdLocation[][]
    headers: string[]
    selectedLocation: AdLocation | null
    keywordsRegexp: RegExp | null
    onSelect: (location: AdLocation) => void
}

interface FilteredOptionsReturn {
    options: AdLocation[]
    groupCounts: number[]
    anyLocation: AdLocation
}

// Списки опций (локаций) по уровням для маленьких экранов (мобильные телефоны, не планшеты).
export function LocationsSelectorOptionsMobile(props: Props) {

    const {
        levels,
        headers,
        selectedLocation,
        keywordsRegexp,
        onSelect,
    } = props

    const virtuoso = useVirtuosoLibAsync<
        AdLocation,
        Pick<Props, 'selectedLocation'>
    >()
    const apiRef = useRef<GroupedVirtuosoHandle | null>(null)

    // Отфильтрованные опции, которые нужно отобразить.
    const filteredOptions: FilteredOptionsReturn = useMemo(
        () => {
            const ret: FilteredOptionsReturn = {
                options: [],
                groupCounts: [],
                anyLocation: {
                    id: 0,
                    name: trans().common.ads_filter.locations_modal.any_location,
                    parentIds: [],
                    children: [],
                },
            }
            levels.forEach((items, depth) => {
                const options = filterOptions(
                    items,
                    depth,
                    keywordsRegexp,
                    selectedLocation
                )
                ret.options.push(...options)
                ret.groupCounts.push(options.length)
            })
            return ret
        },
        [keywordsRegexp, levels, selectedLocation?.id]
    )

    return (
        <>
            <div className="px-2 mb-2">
                <Button
                    key="location-any"
                    color="none"
                    ripple="blue"
                    className={clsx(
                        'location-button',
                        selectedLocation?.id == filteredOptions.anyLocation.id ? 'active' : null
                    )}
                    onClick={() => onSelect(filteredOptions.anyLocation)}
                >
                    {filteredOptions.anyLocation.name}
                </Button>
            </div>
            <div className="locations-group-column flex-1 mt-n2 overflow-hidden">
                <virtuoso.GroupedVirtuoso
                    ref={apiRef}
                    className="flex-1"
                    groupCounts={filteredOptions.groupCounts}
                    context={{
                        selectedLocation,
                    }}
                    groupContent={index => (
                        <div
                            className={'pb-2 group-' + index}
                            onClick={() => {
                                // Проматываем список к заголовку.
                                apiRef.current?.scrollIntoView({
                                    groupIndex: index,
                                    align: 'start',
                                    behavior: 'smooth',
                                })
                            }}
                        >
                            <div className="bg-white fw-500 text-muted text-center px-3 pb-2 pt-1 border-bottom">
                                {headers[index] || ''}
                            </div>
                        </div>
                    )}
                    itemContent={(index, groupIndex, _data, context) => {
                        const optionsInGroup: number = filteredOptions.groupCounts[groupIndex]
                        const option: AdLocation = filteredOptions.options[index]
                        if (optionsInGroup === 1 && option.id === -1) {
                            return (
                                <div className="no-location-found px-3 text-muted">
                                    {trans().common.ads_filter.locations_modal.no_locations_found}
                                </div>
                            )
                        }
                        return (
                            <div className="px-2">
                                <Button
                                    key={'location-' + option.id}
                                    color="none"
                                    ripple="blue"
                                    className={clsx(
                                        'location-button',
                                        filteredOptions.options.length - 1 === index ? 'mb-2' : null,
                                        isLocationSelected(option, context.selectedLocation) ? 'active' : null
                                    )}
                                    onClick={() => {
                                        onSelect(option)
                                        if (
                                            groupIndex < levels.length - 1
                                            && !keywordsRegexp
                                            && option.id > 0
                                        ) {
                                            // Проматываем к следующему уровню локаций.
                                            apiRef.current?.scrollIntoView({
                                                groupIndex: groupIndex + 1,
                                                align: 'start',
                                                behavior: 'smooth',
                                            })
                                        }
                                    }}
                                >
                                    {option.name}
                                </Button>
                            </div>
                        )
                    }}
                />
            </div>
        </>
    )
}

// Фильтрация опций.
function filterOptions(
    locations: AdLocation[],
    depth: number,
    keywordsRegexp: RegExp | null,
    selectedLocation: AdLocation | null
): AdLocation[] {
    let options = locations.filter(location => location.id > 0)
    if (keywordsRegexp) {
        options = options.filter(
            location => keywordsRegexp.test(location.name)
        )
    } else if (
        depth > 0
        && selectedLocation
        && selectedLocation.id > 0
    ) {
        if (selectedLocation.parentIds.length < depth) {
            // Выбрана родительская категория.
            options = locations.filter(
                location => location.parentIds.includes(selectedLocation.id)
            )
        } else if (selectedLocation.parentIds.length === depth) {
            // Выбрана категория такого же уровня вложенности.
            const parentIdsStr = selectedLocation.parentIds.join(',')
            options = locations.filter(
                location => location.parentIds.join(',') === parentIdsStr
            )
        }
    }
    if (options.length === 0) {
        // Опция "ничего не найдено".
        options.push({
            id: -1,
            name: '',
            children: [],
            parentIds: [],
        })
    }
    return options
}
